import { Fragment } from 'react'
import AppDateInput from '../../components/AppDateInput'
import AppDropdown from '../../components/AppDropdown'
import AppPagination from '../../components/AppPagination'
import { useAuditLogScreen } from './hooks/useAuditLogScreen'

function formatTimestamp(value) {
  if (!value) return { day: '-', time: '-' }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return { day: String(value), time: '-' }
  const day = parsed.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
  const time = parsed.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  return { day, time }
}

function actionBadgeClass(action) {
  const normalized = String(action || '').toUpperCase()
  if (normalized.includes('DELETE') || normalized.includes('REJECT')) return 'bg-rose-100 text-rose-800'
  if (normalized.includes('UPDATE') || normalized.includes('EDIT')) return 'bg-amber-100 text-amber-800'
  if (normalized.includes('CREATE') || normalized.includes('APPROVE') || normalized.includes('LOGIN')) return 'bg-emerald-100 text-emerald-800'
  return 'bg-surface-container text-on-surface-variant'
}

function statusBadgeClass(status) {
  const normalized = String(status || '').toUpperCase()
  if (normalized === 'FAILED') return 'bg-rose-100 text-rose-800'
  return 'bg-emerald-100 text-emerald-800'
}

function getDetailLines(row) {
  const summary = row?.detailsSummary || row?.details
  if (summary && typeof summary === 'object') {
    const changes = Array.isArray(summary.changes) ? summary.changes : []
    const notes = Array.isArray(summary.notes) ? summary.notes : []

    // 1. Filter out internal flags (e.g. hasdatechanges, datechangecount)
    const filteredChanges = changes.filter((item) => {
      const f = String(item?.field || '').toLowerCase().replace(/[^a-z0-9]/g, '')
      return !['hasdatechanges', 'datechangecount', 'isarchive', 'isdeleted'].includes(f)
    })

    // 2. Identify date fields already captured in notes with (+/- X days)
    const noteFieldKeys = new Set()
    for (const note of notes) {
      const colonIdx = String(note || '').indexOf(':')
      if (colonIdx > 0) {
        const label = note.slice(0, colonIdx).toLowerCase().replace(/[^a-z0-9]/g, '')
        noteFieldKeys.add(label)
      }
    }

    // 3. Drop duplicate date fields from changes if they are already in notes
    const finalChanges = filteredChanges.filter((item) => {
      const f = String(item?.field || '').toLowerCase().replace(/[^a-z0-9]/g, '')
      for (const nk of noteFieldKeys) {
        if (nk.includes(f) || f.includes(nk) || (f === 'entrydate' && nk.includes('entrydate'))) {
          return false
        }
      }
      return true
    })

    const changeLines = finalChanges.map((item) => `${item.field}: ${item.from} -> ${item.to}`)
    return [...changeLines, ...notes]
  }
  const text = row?.detailsText || ''
  if (typeof text === 'string' && text.trim()) {
    return text.split('|').map((item) => item.trim()).filter(Boolean)
  }
  return []
}

export default function AuditLogScreen() {
  const { state, actions } = useAuditLogScreen()

  return (
    <div className="mx-auto w-full max-w-[1320px] pb-8">
      <header className="mb-6">
        <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight text-on-surface md:text-5xl">Audit Log</h1>
            <p className="mt-2 max-w-3xl text-sm text-on-surface-variant">
              Comprehensive compliance trail for system activities. Track changes and authentication events with filterable logs.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={actions.exportCurrent}
              disabled={state.isExporting}
              className="inline-flex items-center gap-2 rounded-md bg-secondary-container px-4 py-2.5 text-sm font-semibold text-on-secondary-container transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="material-symbols-outlined text-base">download</span>
              <span>{state.isExporting ? 'Exporting...' : 'Export CSV'}</span>
            </button>
            <button
              type="button"
              onClick={actions.refresh}
              className="inline-flex items-center gap-2 rounded-md border border-outline-variant/30 bg-surface-container-lowest px-4 py-2.5 text-sm font-semibold text-on-surface transition hover:bg-surface-container-low"
            >
              <span className="material-symbols-outlined text-base">refresh</span>
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {state.filters.entityId ? (
          <div className="mb-4 flex items-center justify-between rounded-lg border border-primary/20 bg-primary/5 px-4 py-2.5 text-sm text-primary">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-base">history</span>
              <span>
                Viewing change history for <strong>{state.filters.module || 'Item'}</strong> #{state.filters.entityId}
              </span>
            </div>
            <button
              type="button"
              onClick={actions.clearEntityFilter}
              className="inline-flex items-center gap-1 rounded bg-primary/10 px-2 py-1 text-xs font-bold text-primary hover:bg-primary/20 transition"
            >
              <span>Clear Filter / View All</span>
              <span className="material-symbols-outlined text-xs">close</span>
            </button>
          </div>
        ) : null}

        <section className="grid grid-cols-1 gap-3 rounded-xl bg-surface-container-low p-3 md:grid-cols-3 xl:grid-cols-6">
          <label className="grid gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-on-surface-variant">User</span>
            <AppDropdown
              value={state.filters.userId}
              onChange={(value) => actions.setFilter('userId', String(value || ''))}
              options={state.userOptions}
              triggerClassName="rounded-md border-0 bg-surface-container-lowest px-3 py-2 text-sm font-semibold text-on-surface"
            />
          </label>

          <label className="grid gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-on-surface-variant">Module</span>
            <AppDropdown
              value={state.filters.module}
              onChange={(value) => actions.setFilter('module', String(value || ''))}
              options={state.moduleOptions}
              triggerClassName="rounded-md border-0 bg-surface-container-lowest px-3 py-2 text-sm font-semibold text-on-surface"
            />
          </label>

          <label className="grid gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-on-surface-variant">Action</span>
            <AppDropdown
              value={state.filters.action}
              onChange={(value) => actions.setFilter('action', String(value || ''))}
              options={state.actionOptions}
              triggerClassName="rounded-md border-0 bg-surface-container-lowest px-3 py-2 text-sm font-semibold text-on-surface"
            />
          </label>

          <label className="grid gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-on-surface-variant">From</span>
            <AppDateInput
              value={state.filters.dateFrom}
              onChange={(value) => actions.setFilter('dateFrom', value)}
              inputClassName="rounded-md border-0 bg-surface-container-lowest text-sm font-semibold text-on-surface"
            />
          </label>

          <label className="grid gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-on-surface-variant">To</span>
            <AppDateInput
              value={state.filters.dateTo}
              onChange={(value) => actions.setFilter('dateTo', value)}
              inputClassName="rounded-md border-0 bg-surface-container-lowest text-sm font-semibold text-on-surface"
            />
          </label>
        </section>

        {state.error ? (
          <div className="mt-3 rounded-xl border border-error/25 bg-error-container px-4 py-3 text-sm text-error">
            {state.error}
          </div>
        ) : null}
      </header>

      <section className="overflow-hidden rounded-xl border border-outline-variant/15 bg-surface-container-lowest">
        <div className="overflow-x-auto app-hide-scrollbar">
          <table className="w-full min-w-[1100px] border-collapse text-left">
            <thead className="bg-surface-container text-on-surface-variant">
              <tr>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-[0.13em]">Timestamp</th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-[0.13em]">User</th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-[0.13em]">Module</th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-[0.13em]">Action</th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-[0.13em]">Outcome</th>
                <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-[0.13em]">Details</th>
              </tr>
            </thead>

            <tbody>
              {state.isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-14 text-center text-sm text-on-surface-variant">Loading audit logs...</td>
                </tr>
              ) : state.rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-14 text-center text-sm text-on-surface-variant">No audit events found for selected filters.</td>
                </tr>
              ) : (
                state.rows.map((row) => {
                  const stamp = formatTimestamp(row?.log_time)
                  const rowId = String(row?.id || `${row?.log_time}-${row?.actor_name}-${row?.action}`)
                  const isExpanded = state.expandedRowId === rowId
                  const detailLines = getDetailLines(row)
                  return (
                    <Fragment key={rowId}>
                       <tr className="border-b border-surface-container hover:bg-surface-container-low/50">
                        <td className="px-6 py-4 align-top">
                          <p className="text-sm font-semibold text-on-surface">{stamp.day}</p>
                          <p className="text-xs text-on-surface-variant">{stamp.time}</p>
                        </td>
                        <td className="px-6 py-4 align-top">
                          <p className="text-sm font-semibold text-on-surface">{row?.actor_name || 'Unknown User'}</p>
                          <p className="text-xs text-on-surface-variant">{row?.log_type || '-'}</p>
                        </td>
                        <td className="px-6 py-4 align-top">
                          <p className="text-sm font-semibold text-primary">{row?.module || '-'}</p>
                        </td>
                        <td className="px-6 py-4 align-top">
                          <span className={`inline-flex rounded px-2 py-1 text-[10px] font-bold uppercase tracking-[0.11em] ${actionBadgeClass(row?.action)}`}>
                            {String(row?.action || 'UNKNOWN').replaceAll('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 align-top">
                          <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-bold ${statusBadgeClass(row?.status)}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${String(row?.status || 'SUCCESS').toUpperCase() === 'FAILED' ? 'bg-rose-600' : 'bg-emerald-600'}`}></span>
                            {String(row?.status || 'SUCCESS').toUpperCase() === 'FAILED' ? 'Failed' : 'Success'}
                          </span>
                        </td>
                        <td className="px-6 py-4 align-top text-right">
                          <button
                            type="button"
                            onClick={() => actions.toggleExpandedRow(rowId)}
                            className="inline-flex items-center gap-1 text-sm font-bold text-primary hover:underline"
                          >
                            {isExpanded ? 'Hide' : 'View'} Details
                            <span className="material-symbols-outlined text-sm">
                              {isExpanded ? 'expand_less' : 'chevron_right'}
                            </span>
                          </button>
                        </td>
                      </tr>

                      {isExpanded ? (
                        <tr className="bg-surface-container-low">
                          <td colSpan={6} className="px-6 py-5">
                            <article className="rounded-lg border border-outline-variant/20 bg-surface-container-lowest p-4">
                              <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.14em] text-on-surface-variant">Change Summary</p>
                              <div className="rounded bg-surface-container-low p-3 text-xs text-on-surface">
                                {detailLines.length === 0 ? (
                                  <p className="text-on-surface-variant">No field-level change summary available.</p>
                                ) : (
                                  <ul className="space-y-1">
                                    {detailLines.map((line, index) => (
                                      <li key={`${rowId}-detail-${index}`} className="break-words">{line}</li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </article>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col items-start justify-between gap-4 bg-surface-container-low px-6 py-4 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-on-surface-variant">Log Integrity</p>
              <div className="mt-1 flex items-center gap-3">
                <div className="h-2 w-32 overflow-hidden rounded-full bg-surface-variant">
                  <div className="h-full w-[94%] rounded-full bg-primary" />
                </div>
                <span className="text-xs font-bold text-on-surface">94% Healthy</span>
              </div>
            </div>
            <p className="text-xs italic text-on-surface-variant">Showing {state.showRangeStart}-{state.showRangeEnd} of {state.totalCount} entries</p>
          </div>

          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.setPage}
            onPrevious={() => actions.setPage(state.page - 1)}
            onNext={() => actions.setPage(state.page + 1)}
            canPrevious={state.page > 1}
            canNext={state.page < state.totalPages}
          />
        </div>
      </section>
    </div>
  )
}
